import * as THREE from 'three';

/**
 * Supported AR engine types
 */
export enum AREngineType {
  ZAPPAR = 'zappar',
  MINDAR = 'mindar'
}

/**
 * AR tracker visibility state
 */
export enum TrackerState {
  NOT_VISIBLE = 'not_visible',
  VISIBLE = 'visible',
  LOST = 'lost'
}

/**
 * Configuration for AR camera
 */
export interface ARCameraConfig {
  /**
   * Field of view (degrees)
   * Note: Not all AR engines support custom FOV
   */
  fov?: number;

  /**
   * Near clipping plane
   */
  near?: number;

  /**
   * Far clipping plane
   */
  far?: number;

  /**
   * Use rear or front camera
   * Note: Not all AR engines support camera selection
   */
  facingMode?: 'user' | 'environment';
}

/**
 * Configuration for image tracker
 */
export interface TrackerConfig {
  /**
   * Unique identifier for this tracker
   */
  id: string;

  /**
   * Display name
   */
  name: string;

  /**
   * Path to the image target file
   * Format depends on AR engine:
   * - Zappar: .zpt files
   * - MindAR: .mind files
   */
  targetFile: string;

  /**
   * Target index (for MindAR, which can have multiple targets in one .mind file)
   * Default: 0
   */
  targetIndex?: number;

  /**
   * ID of the scene to bind to this tracker
   */
  sceneId: string;

  /**
   * Priority for this tracker when multiple targets are visible
   * Higher number = higher priority
   * Default: 0
   */
  priority?: number;

  /**
   * Optional callback fired the first time this tracker becomes visible
   * Useful for onboarding UI (e.g., hide instructions once the marker is found)
   */
  onFirstVisible?: () => void;

  /**
   * Optional progress callback for loading stages
   * Useful for showing detailed loading progress on mobile
   */
  onProgress?: LoadingProgressCallback;
}

/**
 * Configuration for AR engine initialization
 */
export interface AREngineConfig {
  /**
   * Type of AR engine to use
   */
  engineType: AREngineType;

  /**
   * Container element for AR rendering
   * If not provided, will use document.body
   */
  container?: HTMLElement;

  /**
   * Camera configuration
   */
  cameraConfig?: ARCameraConfig;

  /**
   * Additional engine-specific options
   */
  engineOptions?: {
    // Zappar-specific options
    zappar?: {
      /**
       * Optional loading manager for asset loading
       */
      useLoadingManager?: boolean;
    };

    // MindAR-specific options
    mindar?: {
      /**
       * Filter minimum cutoff frequency (default: 0.001)
       * Lower = smoother but more lag
       */
      filterMinCF?: number;

      /**
       * Filter beta (default: 1000)
       * Higher = more responsive but less smooth
       */
      filterBeta?: number;

      /**
       * Number of consecutive misses before losing track (default: 5)
       */
      missTolerance?: number;

      /**
       * Number of detections needed before tracking (default: 5)
       */
      warmupTolerance?: number;

      /**
       * Maximum number of targets to track simultaneously (default: 1)
       */
      maxTrack?: number;

      /**
       * UI overlay loading and scanning (default: true)
       */
      uiLoading?: boolean;
      uiScanning?: boolean;
    };
  };
}

/**
 * Loading progress callback
 */
export type LoadingProgressCallback = (stage: string, progress: number) => void;

/**
 * Visibility event callback
 */
export type VisibilityCallback = (trackerId: string, state: TrackerState) => void;

/**
 * Permission result
 */
export interface PermissionResult {
  granted: boolean;
  error?: string;
}

/**
 * AR engine capabilities
 */
export interface AREngineCapabilities {
  /**
   * Supports multiple simultaneous trackers
   */
  multipleTrackers: boolean;

  /**
   * Supports switching camera (front/back)
   */
  cameraSwitch: boolean;

  /**
   * Image target file format
   */
  targetFormat: string;

  /**
   * Maximum recommended number of trackers
   */
  maxTrackers?: number;
}
